// =============================================================
// swap.ts — SwapService v4
//  · info():            stock / myMol / paused / 가격(bnbUsd8, molPriceUsd8) / minMol
//  · molForBnb / bnbForMol : 가격 양방향 변환 (프로그래스바용)
//  · checkDistributor():총판 유효성 + 할인/보상
//  · quote():           할인 반영 MOL + 보상/금고 분배
//  · swap():            distributor + minMolOut(슬리피지). tx 상태머신
// =============================================================

import { Contract, Interface, isAddress } from 'ethers';
import { PV_GATE_ABI, ZERO } from './config';
import { ChainClient, hasAddress } from './chain';
import {
  SdkError,
  TxState,
  type DistributorInfo,
  type GateInfo,
  type QuoteResult,
  type TxUpdate,
  type WalletAdapter,
} from './types';

export class SwapService {
  private iface = new Interface(PV_GATE_ABI);

  constructor(
    private wallet: WalletAdapter,
    private chain: ChainClient,
  ) {}

  async info(): Promise<GateInfo> {
    const gate = await this.chain.gateRead();

    // 개별 호출을 격리한다.
    //  · Promise.all 은 하나만 revert 돼도 전체가 실패 → myMol 포함 화면 전부 미표시.
    //  · bnbUsd8() 는 Chainlink 피드 미설정/stale 시 revert 할 수 있으므로
    //    가격 계열이 터져도 잔액(myMol)/재고는 뜨도록 부분 실패를 허용한다.
    const settled = await Promise.allSettled([
      gate.molStock() as Promise<bigint>,       // 0
      gate.mol() as Promise<string>,            // 1
      gate.paused() as Promise<boolean>,        // 2
      gate.bnbUsd8() as Promise<bigint>,        // 3
      gate.molPriceUsd8() as Promise<bigint>,   // 4
      gate.MIN_MOL() as Promise<bigint>,        // 5
    ]);

    const val = <T>(i: number, fallback: T): T =>
      settled[i].status === 'fulfilled'
        ? ((settled[i] as PromiseFulfilledResult<T>).value as T)
        : fallback;

    // 개발 중 어떤 호출이 revert 하는지 콘솔에 남긴다(원인 추적용).
    settled.forEach((s, i) => {
      if (s.status === 'rejected') {
        const fn = ['molStock', 'mol', 'paused', 'bnbUsd8', 'molPriceUsd8', 'MIN_MOL'][i];
        const e = s.reason as { shortMessage?: string; message?: string };
        console.warn(`[GateInfo] ${fn}() 실패:`, e?.shortMessage ?? e?.message ?? s.reason);
      }
    });

    const stock = val<bigint>(0, 0n);
    const molAddr = val<string>(1, ZERO);
    const paused = val<boolean>(2, false);
    const bnbUsd8 = val<bigint>(3, 0n);
    const molPriceUsd8 = val<bigint>(4, 0n);
    const minMol = val<bigint>(5, 0n);

    // 잔액은 가격 계열과 독립적으로 조회한다(핵심: 이게 myMol 미표시의 방어선).
    let myMol: bigint | null = null;
    const me = this.wallet.getAddress();
    if (me && hasAddress(molAddr)) {
      try {
        const erc = await this.chain.erc20(molAddr);
        myMol = (await erc.balanceOf(me)) as bigint;
      } catch {
        myMol = null;
      }
    }
    return { stock, myMol, paused, bnbUsd8, molPriceUsd8, minMol };
  }

  /** BNB(wei) → 받을 기본 MOL(wei) */
  async molForBnb(bnbWei: bigint): Promise<bigint> {
    if (bnbWei <= 0n) return 0n;
    const gate = await this.chain.gateRead();
    return (await gate.molForBnb(bnbWei)) as bigint;
  }

  /** 원하는 MOL(wei) → 필요한 BNB(wei). 프로그래스바용 역산 */
  async bnbForMol(molWei: bigint): Promise<bigint> {
    if (molWei <= 0n) return 0n;
    const gate = await this.chain.gateRead();
    return (await gate.bnbForMol(molWei)) as bigint;
  }

  /** 총판 유효성 + 할인/보상 */
  async checkDistributor(addr: string): Promise<DistributorInfo> {
    const empty: DistributorInfo = {
      exists: false, active: false, discountBp: 0, rewardBp: 0, ownerSet: false,
    };
    if (!addr || !isAddress(addr) || addr === ZERO) return empty;
    try {
      const gate = await this.chain.gateRead();
      const r = (await gate.distributorInfo(addr)) as [boolean, boolean, bigint, bigint, boolean];
      return {
        exists: r[0],
        active: r[1],
        discountBp: Number(r[2]),
        rewardBp: Number(r[3]),
        ownerSet: r[4],
      };
    } catch {
      return empty;
    }
  }

  /** 미리보기: BNB 넣었을 때 할인 MOL + 보상/금고 분배 */
  async quote(bnbWei: bigint, distributor?: string | null): Promise<QuoteResult> {
    if (bnbWei <= 0n)
      return { molOut: 0n, baseMol: 0n, reward: 0n, toTreasury: 0n, discountBp: 0 };
    const dist = distributor && isAddress(distributor) ? distributor : ZERO;
    const gate = await this.chain.gateRead();
    const q = (await gate.quote(bnbWei, dist)) as [bigint, bigint, bigint, bigint, bigint];
    // [molOut, baseMol, reward, toTreasury, disc]
    return {
      molOut: q[0],
      baseMol: q[1],
      reward: q[2],
      toTreasury: q[3],
      discountBp: Number(q[4]),
    };
  }

  /**
   * 교환 실행.
   * @param bnbWei 지불 BNB
   * @param distributor 총판 주소 또는 null
   * @param minMolOut 슬리피지 보호 최소 수령 MOL (0 가능)
   */
  async swap(
    bnbWei: bigint,
    distributor: string | null,
    minMolOut: bigint,
    onState: (u: TxUpdate) => void,
  ): Promise<TxUpdate> {
    const me = this.wallet.getAddress();
    if (!me) throw new SdkError('NO_WALLET');
    if (bnbWei <= 0n) throw new SdkError('ZERO_AMOUNT', '구매 수량을 설정하세요.');

    const bal = await this.chain.bnbBalance(me);
    if (bal <= bnbWei) throw new SdkError('INSUFFICIENT_BNB', 'BNB 잔액이 부족합니다(결제+가스).');

    const dist = distributor && isAddress(distributor) ? distributor : ZERO;
    if (dist !== ZERO && dist.toLowerCase() === me.toLowerCase()) {
      throw new SdkError('UNKNOWN', '총판을 본인 주소로 지정할 수 없습니다.');
    }

    onState({ state: TxState.Signing });
    let gate: Contract;
    try {
      gate = await this.chain.gateWrite();
    } catch (err) {
      throw normalize(err);
    }

    let tx;
    try {
      tx =
        dist === ZERO
          ? await gate.swapNoDistributor(minMolOut, { value: bnbWei })
          : await gate.swap(dist, minMolOut, { value: bnbWei });
    } catch (err) {
      const e = normalize(err);
      onState({ state: TxState.Failed, error: e.code });
      throw e;
    }

    onState({ state: TxState.Pending, hash: tx.hash });
    onState({ state: TxState.Confirming, hash: tx.hash });

    const receipt = await tx.wait(1);
    const molOut = this.parseSwapped(receipt, me);
    const result: TxUpdate = { state: TxState.Success, hash: tx.hash, molOut: molOut ?? undefined };
    onState(result);
    return result;
  }

  private parseSwapped(receipt: unknown, buyer: string): bigint | null {
    const r = receipt as { logs?: Array<{ topics: string[]; data: string }> };
    if (!r.logs) return null;
    for (const log of r.logs) {
      try {
        const parsed = this.iface.parseLog(log);
        if (
          parsed?.name === 'Swapped' &&
          (parsed.args.buyer as string).toLowerCase() === buyer.toLowerCase()
        ) {
          return parsed.args.molOut as bigint;
        }
      } catch {
        /* skip */
      }
    }
    return null;
  }
}

function normalize(err: unknown): SdkError {
  const m =
    (err as { shortMessage?: string; message?: string })?.shortMessage ??
    (err as { message?: string })?.message ??
    String(err);
  if (/user rejected|denied/i.test(m)) return new SdkError('USER_REJECTED');
  if (/insufficient funds/i.test(m)) return new SdkError('INSUFFICIENT_BNB');
  if (/INSUFFICIENT_MOL_STOCK/i.test(m)) return new SdkError('INSUFFICIENT_MOL_STOCK');
  if (/BELOW_MIN_MOL/i.test(m)) return new SdkError('UNKNOWN', '최소 2천만 MOL 이상부터 구매할 수 있습니다.');
  if (/SLIPPAGE/i.test(m)) return new SdkError('UNKNOWN', '가격 변동으로 수량이 변했습니다. 다시 시도하세요.');
  if (/PAUSED/i.test(m)) return new SdkError('GATE_CLOSED');
  if (/SELF_DISTRIBUTOR/i.test(m)) return new SdkError('UNKNOWN', '총판을 본인으로 지정할 수 없습니다.');
  return new SdkError('UNKNOWN', m);
}